import React, { useState, useEffect, useCallback } from "react";
import { useCollection, fmtDate, api } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, Pill, Labeled } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ExternalLink, Edit, Trash2, Radar, RefreshCw, Bookmark, X, TrendingUp, AlertTriangle } from "lucide-react";

const CATEGORIES = ["Money", "Chicago", "Illinois", "Politics", "Dog Industry", "Business", "AI", "Technology", "Sports", "NIL", "Media", "Entertainment", "Africa", "Real Estate", "Competitors", "Opportunities", "Threats", "People"];
const empty = { headline: "", source: "", url: "", category: "Business", summary: "", why_it_matters: "", recommended_action: "", relevance: 3, urgency: 3, opportunity: "", risk: "" };

const PRIO_COLOR = { high: "#E11D48", medium: "#F59E0B", low: "#A3A3A3" };
const KIND_META = { opportunity: { color: "#10B981", icon: TrendingUp, label: "Opportunity" }, risk: { color: "#E11D48", icon: AlertTriangle, label: "Risk" } };

export default function Intelligence() {
  const { items, loading, create, update, remove, load } = useCollection("intelligence");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [rss, setRss] = useState([]);
  const [rssLoading, setRssLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadRss = useCallback(async () => {
    setRssLoading(true);
    try { const { data } = await api.get("/intelligence/rss"); setRss(data); }
    catch { /* ignore */ } finally { setRssLoading(false); }
  }, []);
  useEffect(() => { loadRss(); }, [loadRss]);

  const refreshFeeds = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/intelligence/rss/refresh", {});
      toast.success(`${data.new} new intelligence item${data.new === 1 ? "" : "s"}`);
      await loadRss();
    } catch { toast.error("Could not refresh feeds"); } finally { setRefreshing(false); }
  };

  const saveRss = async (id) => {
    try { await api.post(`/intelligence/rss/${id}/save`); toast.success("Saved to Intelligence"); await Promise.all([loadRss(), load()]); }
    catch { toast.error("Could not save"); }
  };
  const dismissRss = async (id) => {
    try { await api.delete(`/intelligence/rss/${id}`); await loadRss(); } catch { /* ignore */ }
  };

  const openNew = () => { setForm(empty); setEditId(null); setDlg(true); };
  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditId(i.id); setDlg(true); };
  const save = async () => {
    if (!form.headline.trim()) { toast.error("Headline required"); return; }
    const payload = { ...form, relevance: Number(form.relevance), urgency: Number(form.urgency), date: form.date || new Date().toISOString() };
    if (editId) await update(editId, payload); else await create(payload);
    toast.success("Saved"); setDlg(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader title="Intelligence" subtitle="Automated RSS feeds + your manual records — grounded, no fabrication." testId="intel-header"
        action={
          <div className="flex items-center gap-2">
            <button data-testid="rss-refresh-btn" onClick={refreshFeeds} disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-edge text-[#FAFAFA] font-semibold text-sm hover:border-gold/40 transition-colors disabled:opacity-60">
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh feeds"}
            </button>
            <button data-testid="intel-new-btn" onClick={openNew} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><Plus size={16} /> Add</button>
          </div>
        } />

      {/* Automated Intelligence (RSS) */}
      <div data-testid="rss-section">
        <div className="flex items-center gap-2 mb-3">
          <Radar size={16} className="text-gold" />
          <span className="label-caps text-gold">Automated Intelligence</span>
          <span className="text-xs text-[#5a5a5a]">Chicago · East Africa · NIL · Bears</span>
        </div>
        {rssLoading ? <Loading label="Loading feeds…" /> : rss.length === 0 ? (
          <EmptyState testId="rss-empty" title="No automated intelligence yet." hint="Tap “Refresh feeds” to pull the latest news relevant to your ventures."
            action={<button onClick={refreshFeeds} disabled={refreshing} className="px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm">{refreshing ? "Refreshing…" : "Refresh feeds"}</button>} />
        ) : (
          <div className="space-y-3">
            {rss.map((r) => {
              const km = KIND_META[r.kind];
              const KIcon = km?.icon;
              return (
                <div key={r.id} data-testid={`rss-${r.id}`} className="bg-surface border border-edge rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill color={PRIO_COLOR[r.priority] || "#A3A3A3"} solid>{(r.priority || "medium").toUpperCase()}</Pill>
                        <span className="font-semibold">{r.headline}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <Pill color="#D4AF37">{r.project_category || "General"}</Pill>
                        {km && <Pill color={km.color}>{KIcon && <KIcon size={11} />} {km.label}</Pill>}
                        <span className="text-xs text-[#A3A3A3]">{r.source}</span>
                      </div>
                      {r.why_it_matters && <p className="text-sm mt-2"><span className="label-caps">Why: </span>{r.why_it_matters}</p>}
                      {r.recommended_action && <p className="text-sm mt-1"><span className="label-caps">Action: </span>{r.recommended_action}</p>}
                      <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gold mt-2"><ExternalLink size={12} /> Read source</a>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button data-testid={`rss-save-${r.id}`} onClick={() => saveRss(r.id)} title="Save to Intelligence" className="text-[#A3A3A3] hover:text-gold p-1"><Bookmark size={15} /></button>
                      <button data-testid={`rss-dismiss-${r.id}`} onClick={() => dismissRss(r.id)} title="Dismiss" className="text-[#A3A3A3] hover:text-sred p-1"><X size={15} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Edit size={15} className="text-[#A3A3A3]" />
        <span className="label-caps">Manual Records</span>
      </div>

      {items.length === 0 ? <EmptyState testId="intel-empty" title="No intelligence captured yet." hint="Record signals, opportunities, and threats worth acting on." action={<button onClick={openNew} className="px-4 h-9 rounded-lg bg-gold text-ink font-bold text-sm">+ Add Intelligence</button>} /> : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} data-testid={`intel-${i.id}`} className="bg-surface border border-edge rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><Radar size={15} className="text-gold shrink-0" /><span className="font-semibold">{i.headline}</span><Pill color="#D4AF37">{i.category}</Pill></div>
                  {i.summary && <p className="text-sm text-[#A3A3A3] mt-1">{i.summary}</p>}
                  {i.why_it_matters && <p className="text-sm mt-2"><span className="label-caps">Why: </span>{i.why_it_matters}</p>}
                  {i.recommended_action && <p className="text-sm mt-1"><span className="label-caps">Action: </span>{i.recommended_action}</p>}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {i.source && <span className="text-xs text-[#A3A3A3]">{i.source}</span>}
                    {i.url && <a href={i.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-gold"><ExternalLink size={12} /> Source</a>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0"><button onClick={() => openEdit(i)} className="text-[#A3A3A3] hover:text-gold p-1"><Edit size={14} /></button><button onClick={async () => { await remove(i.id); }} className="text-[#A3A3A3] hover:text-sred p-1"><Trash2 size={14} /></button></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editId ? "Edit" : "Add"} Intelligence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Labeled label="Headline"><Input data-testid="intel-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Category"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-9 rounded-md bg-ink border border-edge px-2 text-sm">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Labeled>
              <Labeled label="Source"><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="URL"><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Summary"><Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Why it matters"><Input value={form.why_it_matters} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <Labeled label="Recommended action"><Input value={form.recommended_action} onChange={(e) => setForm({ ...form, recommended_action: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="intel-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
